import { CHAT_BOT_DEFAULT_SETTINGS } from "@jugl-web/domain-resources/chat-bot/consts";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { isValidHttpUrl, useTranslations } from "@jugl-web/utils";
import {
  ANONYMOUS_SESSION_ENTITY_ID_KEY,
  CX_BUSINESS_NAME_KEY,
  CX_TRIAL_PROCEEDED_KEY,
  getStorageItem,
  saveItemToStorage,
} from "@jugl-web/utils/storage";
import { NavigateToDefaultEntity } from "@web-src/modules/navigation/components/NavigateToDefaultEntity/NavigateToDefaultEntity";
import { selectAuthState } from "@web-src/modules/auth/store/authSlice";
import { useAnonymousSession } from "@web-src/modules/auth/providers/AnonymousSessionProvider";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { TrialFullPageLoader } from "@web-src/modules/trial/pages/CreateBoardInTrial/components/TrialFullPageLoader/TrialFullPageLoader";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { useUpdateChatBotSettings } from "@jugl-web/domain-resources/chat-bot/hooks/useUpdateChatBotSettings";
import {
  Button,
  InteractiveContainer,
  TextInput,
  DataLoadingWrapper,
  MultiSectionLayout,
} from "@jugl-web/ui-kit";
import { KnowledgeItem } from "@jugl-web/rest-api/ai-workflows/types";
import { useStartSessionRecording } from "@web-src/modules/common/utils/posthogSetup";
import { ReactComponent as AlertIcon } from "./assets/alert.svg";
import { ReactComponent as Logo } from "./assets/logo.svg";
import { OverviewVideo } from "./components/OverviewVideo";
import { TrialHelpWidget } from "./components/TrialHelpWidget";
import { useCXSignupDialog } from "../../components/CXSignupDialog/useCXSignupDialog";
import { useKnowledgeTraining } from "../../providers/KnowledgeTrainingProvider";

class TrainAgentFailed extends Error {}

const CXTrialPageContent: React.FC = () => {
  const { t } = useTranslations();
  const { aiWorkflowsApi, integrationsApi } = useRestApiProvider();
  const { navigateToPage } = useNavigation();
  const { entityId } = useAnonymousSession();
  const CXSignupDialog = useCXSignupDialog();

  const [getListLinks] = aiWorkflowsApi.useLazyGetListLinksQuery();
  const [uploadLinkKnowledge] = aiWorkflowsApi.useUploadLinkKnowledgeMutation();
  const [updateAgentConfig] = integrationsApi.useUpdateAgentConfigMutation();
  const [getSiteInfoQuery] = aiWorkflowsApi.useLazyGetSiteInfoQuery();
  const [deleteKnowledge] = aiWorkflowsApi.useDeleteKnowledgeMutation();
  const { trainAgent } = useKnowledgeTraining();

  const [searchParams, setSearchParams] = useSearchParams();
  const buildParam = searchParams.get("build");
  const [link, setLink] = useState(buildParam || "");
  const [error, setError] = useState<"empty" | "format" | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const loaderDoneRef = useRef<(() => void) | null>(null);

  const { updateChatBotSettings } = useUpdateChatBotSettings({ entityId });

  const {
    data: linksKnowledge,
    isLoading: isLinksKnowledgeLoading,
    isError: isLinksKnowledgeError,
    refetch: linksKnowledgeRefetch,
  } = aiWorkflowsApi.useGetKnowledgeQuery({
    agentId: entityId,
    entityId,
    type: "links",
    meta: { silent: true },
  });
  const {
    data: existingAgentConfig,
    isLoading: isAgentConfigLoading,
    isError: isAgentConfigError,
    refetch: refetchAgentConfig,
  } = integrationsApi.useGetAiAgentConfigQuery({
    entityId,
    meta: { silent: true },
  });

  const linkKnowledge = linksKnowledge?.[0] as
    | KnowledgeItem<"links">
    | undefined;

  useStartSessionRecording();

  useEffect(() => {
    const existingLink = linkKnowledge?.data.ref;
    if (!existingLink) return;

    setLink(existingLink);
  }, [linkKnowledge?.data.ref]);

  useEffect(() => {
    const hasProceeded = getStorageItem<boolean>(CX_TRIAL_PROCEEDED_KEY);
    if (hasProceeded) {
      localStorage.removeItem(CX_TRIAL_PROCEEDED_KEY);
      localStorage.removeItem("session");
      localStorage.removeItem(ANONYMOUS_SESSION_ENTITY_ID_KEY);
      window.location.reload();
    }
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!link) {
      setError("empty");
      return;
    }
    if (!isValidHttpUrl(link, false)) {
      setError("format");
      return;
    }
    setError(null);
    setIsSubmitting(true);

    const minDisplay = new Promise<void>((resolve) => {
      loaderDoneRef.current = resolve;
    });

    try {
      const linkWithProtocol = /^https?:\/\//i.test(link)
        ? link
        : `https://${link}`;

      const work = (async () => {
        const existingLinkKnowledge = linkKnowledge;
        const hasSameLink =
          !!existingLinkKnowledge &&
          existingLinkKnowledge.data.ref === linkWithProtocol;

        let knowledgeSourceId: string;
        let siteInfo: { title?: string; logo_base64?: string } | null;

        if (hasSameLink) {
          const result = await getSiteInfoQuery({
            agentId: entityId,
            entityId,
            url: linkWithProtocol,
          }).unwrap();
          knowledgeSourceId = existingLinkKnowledge.data.id;
          siteInfo = result.info;
        } else {
          if (existingLinkKnowledge) {
            await deleteKnowledge({
              agentId: entityId,
              entityId,
              type: "links",
              source_id: existingLinkKnowledge.data.id,
            }).unwrap();
          }
          const [siteInfoResult, listLinks] = await Promise.all([
            getSiteInfoQuery({
              agentId: entityId,
              entityId,
              url: linkWithProtocol,
            }).unwrap(),
            getListLinks({
              agentId: entityId,
              entityId,
              params: { url: linkWithProtocol },
            }).unwrap(),
          ]);
          const knowledgeSource = await uploadLinkKnowledge({
            agentId: entityId,
            entityId,
            url: listLinks.links.url,
            links: listLinks.links.links,
          }).unwrap();
          knowledgeSourceId = knowledgeSource.id;
          siteInfo = siteInfoResult.info;
        }

        const businessName = siteInfo?.title?.trim();
        await Promise.all([
          businessName && existingAgentConfig
            ? (async () => {
                saveItemToStorage(CX_BUSINESS_NAME_KEY, businessName);
                await updateAgentConfig({
                  entityId,
                  config: {
                    ...existingAgentConfig.config,
                    business_name: businessName,
                  },
                }).unwrap();
              })()
            : Promise.resolve(),
          updateChatBotSettings({
            agentImage: siteInfo?.logo_base64 || "",
            agentName: businessName
              ? `${businessName} ${t({
                  id: "cx-trial-page.ai-agent",
                  defaultMessage: "AI Agent",
                })}`
              : CHAT_BOT_DEFAULT_SETTINGS.agentName,
          }),
        ]);

        const ok = await trainAgent([knowledgeSourceId]);
        if (!ok) {
          throw new TrainAgentFailed();
        }
      })();

      await Promise.all([work, minDisplay]);
      saveItemToStorage(CX_TRIAL_PROCEEDED_KEY, "true");
      navigateToPage("cxAnonymousAIAgents", undefined, { entityId });
    } catch {
      setIsSubmitting(false);
    }
  }, [
    link,
    entityId,
    linkKnowledge,
    existingAgentConfig,
    getSiteInfoQuery,
    getListLinks,
    uploadLinkKnowledge,
    deleteKnowledge,
    updateAgentConfig,
    updateChatBotSettings,
    trainAgent,
    navigateToPage,
    t,
  ]);

  const hasPrefetchedBuildParam = useRef(false);
  useEffect(() => {
    if (
      buildParam &&
      !hasPrefetchedBuildParam.current &&
      !isLinksKnowledgeLoading &&
      !isLinksKnowledgeError &&
      !linkKnowledge
    ) {
      hasPrefetchedBuildParam.current = true;
      handleSubmit();
      setSearchParams(
        (prev) => {
          prev.delete("build");
          return prev;
        },
        { replace: true }
      );
    }
  }, [
    buildParam,
    handleSubmit,
    isLinksKnowledgeError,
    isLinksKnowledgeLoading,
    linkKnowledge,
    setSearchParams,
  ]);

  return (
    <>
      {isSubmitting && (
        <TrialFullPageLoader onComplete={() => loaderDoneRef.current?.()} />
      )}
      <MultiSectionLayout
        classNames={{ content: "flex flex-col" }}
        className="min-h-screen w-screen [@media(min-width:1280px)]:h-screen"
        leftSide={
          <div className="hidden h-screen w-[960px] max-w-[min(50vw,700px)] overflow-hidden [@media(min-width:1300px)]:flex [@media(min-width:1440px)]:max-w-[min(50vw,960px)]">
            <OverviewVideo />
          </div>
        }
      >
        <DataLoadingWrapper
          isLoading={isLinksKnowledgeLoading || isAgentConfigLoading}
          isError={isLinksKnowledgeError || isAgentConfigError}
          onRetry={() => {
            if (isLinksKnowledgeError) {
              linksKnowledgeRefetch();
            }
            if (isAgentConfigError) {
              refetchAgentConfig();
            }
          }}
          className="flex min-h-screen flex-col"
        >
          <div className="relative flex w-full grow flex-col items-center justify-center">
            <Logo className="absolute top-[40px] right-[40px] z-20" />
            <div className="relative z-10 flex w-[400px] flex-col items-center justify-center gap-12 py-[110px] [@media(min-width:1280px)]:w-[420px] [@media(min-width:1280px)]:py-6 [@media(min-width:1440px)]:w-[440px] [@media(min-width:1440px)]:gap-14 [@media(min-width:1728px)]:w-[500px]">
              <div className="flex flex-col items-center gap-1.5 text-center">
                <span className="text-center font-secondary text-[26px] leading-[150%] font-semibold text-dark [@media(min-width:1280px)]:text-3xl [@media(min-width:1440px)]:text-[30px] [@media(min-width:1728px)]:text-[34px]">
                  {t({
                    id: "cx-trial-page.build-your-business-ai-agent",
                    defaultMessage: "Build Your Business AI Agent",
                  })}
                </span>
                <span className="font-secondary text-[13px] leading-[150%] text-dark-600 [@media(min-width:1440px)]:text-sm">
                  {t({
                    id: "cx-trial-page.add-website-link",
                    defaultMessage:
                      "Add your Website link to train AI Agent based on it",
                  })}
                </span>
              </div>
              <div className="flex w-full flex-col gap-8">
                <TextInput
                  labelPosition="border-overlap"
                  label={t({
                    id: "cx-trial-page.website-link",
                    defaultMessage: "Website link",
                  })}
                  placeholder="mybusiness.com"
                  value={link}
                  onChange={(e) => {
                    setLink(e.target.value.trim());
                    if (error) setError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }}
                  isInvalid={error === "format"}
                  errorMessage={
                    error === "format"
                      ? t({
                          id: "validation.invalid-url",
                          defaultMessage: "Enter a valid URL address",
                        })
                      : undefined
                  }
                />
                {error === "empty" && (
                  <div className="flex items-center justify-start gap-3 rounded-xl bg-tertiary-50 p-3">
                    <AlertIcon className="shrink-0" />
                    <span className="font-secondary text-sm leading-[140%] text-dark">
                      {t({
                        id: "cx-trial-page.validation-website-link",
                        defaultMessage:
                          "Add the Website Link to build AI Agent",
                      })}
                    </span>
                  </div>
                )}
              </div>
              <Button fullWidth onClick={handleSubmit}>
                {t({
                  id: "cx-trial-page.continue-button",
                  defaultMessage: "Train & Build",
                })}
              </Button>
            </div>
            <div className="absolute bottom-10 left-1/2 z-0 flex h-12 -translate-x-1/2 items-center justify-center">
              <InteractiveContainer
                className="font-secondary text-sm leading-[21px] text-grey-800 underline transition-colors hover:text-[#2F80ED]"
                onClick={() => CXSignupDialog?.open()}
              >
                {t({
                  id: "cx-trial-page.sign-up",
                  defaultMessage: "Sign-up",
                })}
              </InteractiveContainer>
            </div>
          </div>
          <div className="min-h-min w-full overflow-hidden [@media(min-width:1300px)]:hidden">
            <OverviewVideo />
          </div>
          {!isSubmitting && <TrialHelpWidget />}
        </DataLoadingWrapper>
      </MultiSectionLayout>
    </>
  );
};

export const CXTrialPage: React.FC = () => {
  const { isAuthenticated } = useSelector(selectAuthState);
  const { isAnonymous } = useAnonymousSession();

  if (isAuthenticated) {
    return <NavigateToDefaultEntity />;
  }

  if (!isAnonymous) {
    return (
      <MultiSectionLayout
        className="min-h-screen w-screen [@media(min-width:1280px)]:h-screen"
        leftSide={
          <div className="hidden h-screen w-[960px] max-w-[min(50vw,700px)] overflow-hidden [@media(min-width:1300px)]:flex [@media(min-width:1440px)]:max-w-[min(50vw,960px)]">
            <OverviewVideo />
          </div>
        }
      >
        <DataLoadingWrapper isLoading>
          <div />
        </DataLoadingWrapper>
      </MultiSectionLayout>
    );
  }

  return <CXTrialPageContent />;
};

export default CXTrialPage;
